import { useState, useEffect, useRef, useCallback } from "react";
import config from "../config";

const api = config.API_BASE_URL;
const chatMgmt = config.API_BASE_URL;

// ─── Helpers ──────────────────────────────────────────────────
const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};
const formatDay = (ts) => {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

// ─── Profile Picture Cache ────────────────────────────────────
// ─── Profile Picture Cache ────────────────────────────────────
const PIC_CACHE = {};
async function fetchProfilePic(username, isAdmin = false) {
  if (!username) return null;
  if (PIC_CACHE[username] !== undefined) return PIC_CACHE[username];
  PIC_CACHE[username] = null; // reserve slot

  // Always try Admin endpoint first for admin_ prefixed names
  const isAdminUsername = username.toLowerCase().startsWith("admin_");
  const cleanName = username.replace(/^admin_/i, "");
  const prefixedName = `admin_${cleanName}`;

  const endpoints = isAdminUsername
    ? [
      `${api}/Admin/get-profile-picture/${encodeURIComponent(username)}`,
      `${api}/Admin/get-profile-picture/${encodeURIComponent(cleanName)}`,
      `${api}/Users/get-profile-picture/${encodeURIComponent(username)}`,
    ]
    : [
      `${api}/Users/get-profile-picture/${encodeURIComponent(username)}`,
      `${api}/Admin/get-profile-picture/${encodeURIComponent(prefixedName)}`,
      `${api}/Admin/get-profile-picture/${encodeURIComponent(username)}`,
    ];

  try {
    for (const url of endpoints) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          const blob = await res.blob();
          const objectUrl = URL.createObjectURL(blob);
          // Store under ALL possible keys
          PIC_CACHE[username] = objectUrl;
          PIC_CACHE[cleanName] = objectUrl;
          PIC_CACHE[prefixedName] = objectUrl;
          return objectUrl;
        }
      } catch { }
    }
  } catch { }

  PIC_CACHE[username] = null;
  return null;
}

const REACTIONS = [
  {
    emoji: <svg viewBox="0 0 40 40" width="26" height="26"><circle cx="20" cy="20" r="20" fill="#f33e58" /><path d="M20 30s-9-5.5-9-12a5 5 0 0110 0 5 5 0 0110 0c0 6.5-9 12-9 12z" fill="white" opacity="0.95" /><path d="M20 30s-9-5.5-9-12a5 5 0 0110 0 5 5 0 0110 0c0 6.5-9 12-9 12z" fill="#ff6b81" /></svg>,
    label: "Love", color: "#f33e58"
  },
  {
    emoji: <svg viewBox="0 0 40 40" width="26" height="26"><circle cx="20" cy="20" r="20" fill="#f7b125" /><circle cx="14" cy="17" r="2.5" fill="#7a4500" /><circle cx="26" cy="17" r="2.5" fill="#7a4500" /><path d="M13 25c2 3 12 3 14 0" stroke="#7a4500" strokeWidth="2" strokeLinecap="round" fill="none" /><path d="M10 16c1-3 4-4 6-3M30 16c-1-3-4-4-6-3" stroke="#7a4500" strokeWidth="1.5" strokeLinecap="round" fill="none" /></svg>,
    label: "Haha", color: "#f7b125"
  },
  {
    emoji: <svg viewBox="0 0 40 40" width="26" height="26"><circle cx="20" cy="20" r="20" fill="#f7b125" /><circle cx="14" cy="17" r="2.5" fill="#7a4500" /><circle cx="26" cy="17" r="2.5" fill="#7a4500" /><ellipse cx="20" cy="27" rx="5" ry="6" fill="#7a4500" /><ellipse cx="20" cy="28" rx="4" ry="4.5" fill="#ff4444" /></svg>,
    label: "Wow", color: "#f7b125"
  },
  {
    emoji: <svg viewBox="0 0 40 40" width="26" height="26"><circle cx="20" cy="20" r="20" fill="#f7b125" /><circle cx="14" cy="17" r="2.5" fill="#7a4500" /><circle cx="26" cy="17" r="2.5" fill="#7a4500" /><path d="M13 28c2-2 12-2 14 0" stroke="#7a4500" strokeWidth="2" strokeLinecap="round" fill="none" /><path d="M12 14c1-2 4-2 5-1M28 14c-1-2-4-2-5-1" stroke="#7a4500" strokeWidth="1.5" strokeLinecap="round" /><ellipse cx="15" cy="23" rx="3" ry="2" fill="#4fc3f7" opacity="0.8" /><ellipse cx="25" cy="23" rx="3" ry="2" fill="#4fc3f7" opacity="0.8" /></svg>,
    label: "Sad", color: "#f7b125"
  },
  {
    emoji: <svg viewBox="0 0 40 40" width="26" height="26"><circle cx="20" cy="20" r="20" fill="#e9710f" /><circle cx="14" cy="19" r="2.5" fill="#7a2500" /><circle cx="26" cy="19" r="2.5" fill="#7a2500" /><path d="M13 27c2-2 12-2 14 0" stroke="#7a2500" strokeWidth="2" strokeLinecap="round" fill="none" /><path d="M10 15c2-3 8-4 10-2M30 15c-2-3-8-4-10-2" stroke="#7a2500" strokeWidth="2" strokeLinecap="round" /></svg>,
    label: "Angry", color: "#e9710f"
  },
  {
    emoji: <svg viewBox="0 0 40 40" width="26" height="26"><circle cx="20" cy="20" r="20" fill="#0084ff" /><rect x="9" y="15" width="22" height="14" rx="4" fill="white" opacity="0.15" /><path d="M14 20l4 4 8-8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>,
    label: "Like", color: "#0084ff"
  },
];

// ─── Avatar ───────────────────────────────────────────────────
function Avatar({ name, size = 38, online = false, picUrl = null, isAdmin = false }) {
  const USER_COLORS = ["#0084ff", "#00b09b", "#f7971e", "#e040fb", "#f44336", "#43a047", "#ff5722", "#3f51b5"];
  const ADMIN_COLORS = ["#7c3aed", "#db2777", "#059669", "#d97706", "#dc2626", "#0891b2", "#65a30d", "#c026d3"];
  const palette = isAdmin ? ADMIN_COLORS : USER_COLORS;
  const color = palette[name?.charCodeAt(0) % palette.length] ?? "#0084ff";
  const [err, setErr] = useState(false);

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      {picUrl && !err ? (
        <img src={picUrl} alt={name} onError={() => setErr(true)}
          style={{
            width: size, height: size, borderRadius: "50%", objectFit: "cover",
            display: "block",
            border: isAdmin ? "2.5px solid rgba(124,58,237,0.5)" : "2.5px solid rgba(255,255,255,0.12)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
          }} />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: "50%",
          background: `linear-gradient(135deg, ${color}, ${color}bb)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 800, fontSize: size * 0.38,
          userSelect: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          border: isAdmin ? "2.5px solid rgba(124,58,237,0.4)" : "2.5px solid rgba(255,255,255,0.1)",
          position: "relative",
        }}>
          {name?.[0]?.toUpperCase() ?? "?"}
          {isAdmin && (
            <div style={{
              position: "absolute", bottom: -2, right: -2,
              width: size * 0.32, height: size * 0.32,
              background: "linear-gradient(135deg,#7c3aed,#db2777)",
              borderRadius: "50%", border: "1.5px solid #0f1117",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: size * 0.14,
            }}>⚙</div>
          )}
        </div>
      )}
      {online && (
        <span style={{
          position: "absolute", bottom: 1, right: isAdmin ? 6 : 1,
          width: size * 0.27, height: size * 0.27, borderRadius: "50%",
          background: "#31a24c",
          border: "2px solid #0f1117",
          boxShadow: "0 0 0 2px rgba(49,164,76,0.35)",
          animation: "pulse-green 2s infinite",
        }} />
      )}
    </div>
  );
}

// ─── Reaction Picker ──────────────────────────────────────────
function ReactionPicker({ onPick, onClose, isAdmin, currentReaction, isDark }) {
  return (
    <div onMouseLeave={onClose} style={{
      position: "absolute",
      bottom: "calc(100% + 10px)",
      [isAdmin ? "right" : "left"]: -8,
      background: isDark ? "rgba(22,25,40,0.95)" : "rgba(255,255,255,0.98)",
      backdropFilter: "blur(20px)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 999, padding: "8px 14px",
      display: "flex", gap: 4,
      boxShadow: isDark ? "0 12px 40px rgba(0,0,0,0.6)" : "0 12px 40px rgba(0,0,0,0.18)",
      zIndex: 400,
      animation: "pop-spring 0.22s cubic-bezier(.34,1.56,.64,1)",
    }}>
      {REACTIONS.map(({ emoji, label, color }) => {
        const isActive = currentReaction === emoji;
        return (
          <div key={emoji} style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <button onClick={() => { onPick(emoji); onClose(); }} title={label}
              style={{
                background: isActive ? `${color}22` : "none",
                border: "none", cursor: "pointer", fontSize: 26, padding: "4px", borderRadius: "50%",
                width: 46, height: 46, display: "flex", alignItems: "center", justifyContent: "center",
                transition: "transform 0.18s cubic-bezier(.34,1.56,.64,1)",
                transform: isActive ? "scale(1.3)" : "scale(1)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "scale(1.55) translateY(-8px)";
                e.currentTarget.parentNode.querySelector(".tip").style.opacity = "1";
                e.currentTarget.parentNode.querySelector(".tip").style.transform = "translateX(-50%) scale(1)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = isActive ? "scale(1.3)" : "scale(1)";
                e.currentTarget.parentNode.querySelector(".tip").style.opacity = "0";
                e.currentTarget.parentNode.querySelector(".tip").style.transform = "translateX(-50%) scale(0.7)";
              }}
            >{emoji}</button>
            <span className="tip" style={{
              position: "absolute", bottom: -22, left: "50%",
              transform: "translateX(-50%) scale(0.7)",
              background: "rgba(0,0,0,0.8)", color: "#fff",
              fontSize: 10, fontWeight: 700, padding: "3px 8px",
              borderRadius: 8, whiteSpace: "nowrap", opacity: 0,
              transition: "all 0.14s ease", pointerEvents: "none",
            }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Reply Bar ────────────────────────────────────────────────
function ReplyBar({ replyTo, onCancel, isDark }) {
  if (!replyTo) return null;
  const isImg = replyTo.content?.startsWith("[IMAGE]");
  const preview = isImg ? "📷 Photo" : replyTo.content?.slice(0, 70);
  const clean = preview?.startsWith("↩") ? preview.replace(/^↩ ".*?" — /, "") : preview;
  return (
    <div style={{
      padding: "10px 18px",
      background: isDark ? "rgba(0,132,255,0.08)" : "rgba(0,132,255,0.05)",
      borderTop: "2px solid rgba(0,132,255,0.5)",
      display: "flex", alignItems: "center", gap: 10,
      animation: "slide-up 0.15s ease",
    }}>
      <div style={{ width: 3, borderRadius: 3, alignSelf: "stretch", background: "linear-gradient(180deg,#0084ff,#00c6ff)", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#0084ff", marginBottom: 1 }}>
          Replying to {replyTo.senderType === "admin" ? "yourself" : replyTo.user}
        </div>
        <div style={{ fontSize: 12, color: isDark ? "#9ca3af" : "#65676b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {clean}
        </div>
      </div>
      <button onClick={onCancel} style={{
        background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)",
        border: "none", borderRadius: "50%", width: 26, height: 26,
        cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        color: isDark ? "#9ca3af" : "#65676b", fontSize: 13, fontWeight: 900,
      }}>✕</button>
    </div>
  );
}

// ─── Reply Snippet ────────────────────────────────────────────
function ReplySnippet({ quotedContent, isAdmin, isDark, activeUser, quotedSenderType }) {
  const isImg = quotedContent?.startsWith("[IMAGE]");
  const text = isImg ? "📷 Photo" : quotedContent?.slice(0, 80);
  const label = quotedSenderType === "admin" ? "You" : (activeUser || "User");
  return (
    <div style={{ borderLeft: `3px solid ${isAdmin ? "rgba(255,255,255,0.5)" : "#0084ff"}`, paddingLeft: 8, marginBottom: 6, borderRadius: "2px 4px 4px 2px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: isAdmin ? "rgba(255,255,255,0.85)" : "#0084ff", marginBottom: 1 }}>{label}</div>
      <div style={{ fontSize: 11.5, color: isAdmin ? "rgba(255,255,255,0.72)" : (isDark ? "#9ca3af" : "#65676b"), lineHeight: 1.35 }}>{text}</div>
    </div>
  );
}

// ─── Image Preview ────────────────────────────────────────────
function ImagePreview({ file, onRemove }) {
  const [url] = useState(() => URL.createObjectURL(file));
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <img src={url} alt="preview" style={{ height: 76, borderRadius: 12, border: "2px solid #0084ff", objectFit: "cover", display: "block" }} />
      <button onClick={onRemove} style={{
        position: "absolute", top: -7, right: -7,
        background: "#e41e3f", color: "#fff", border: "2.5px solid #0f1117", borderRadius: "50%",
        width: 22, height: 22, cursor: "pointer", fontSize: 11, fontWeight: 900,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>✕</button>
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────────
function MessageBubble({ msg, isMyMsg, prevMsg, nextMsg, onReply, onDelete, onPin, onReact, isDark, pinnedId, activeUser, userPicUrl, isAdminChat, canDelete }) {
  const [hovered, setHovered] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const timerRef = useRef(null);

  const sameAuthorPrev = prevMsg?.senderType === msg.senderType && prevMsg?.admin === msg.admin;
  const sameAuthorNext = nextMsg?.senderType === msg.senderType && nextMsg?.admin === msg.admin;
  const showTime = !nextMsg || new Date(nextMsg.timestamp) - new Date(msg.timestamp) > 300000;
  const isPinned = String(pinnedId) === String(msg.messageId);

  const rawContent = msg.content ?? "";
  const isImage = rawContent.startsWith("[IMAGE]");
  const imagePayload = isImage ? rawContent.replace("[IMAGE]", "").trim() : null;
  const imageUrl = imagePayload
    ? (imagePayload.startsWith("data:") || imagePayload.startsWith("http")
      ? imagePayload
      : `data:image/png;base64,${imagePayload}`)
    : null;

  const isReply = !isImage && rawContent.startsWith("↩");
  let quotedText = null, quotedSenderType = null, actualContent = rawContent;
  if (isReply) {
    const m = rawContent.match(/^↩ "(.*?)" — (.*)$/s);
    if (m) {
      quotedText = m[1];
      actualContent = m[2];
      quotedSenderType = isMyMsg ? "admin" : "user";
    }
  }

  const hasReply = quotedText || msg.replyToContent;
  const displayQuote = quotedText || msg.replyToContent;
  const displayQuoteSender = quotedSenderType
    || (msg.replyToSender && msg.replyToSender !== msg.admin ? "user" : "admin");

  const reaction = msg.reaction || (msg.likes ? "❤️" : null);

  const otherAdminBg = isDark
    ? "linear-gradient(135deg, rgba(124,58,237,0.3), rgba(219,39,119,0.2))"
    : "linear-gradient(135deg, rgba(124,58,237,0.12), rgba(219,39,119,0.08))";

  return (
    <div style={{
      display: "flex",
      flexDirection: isMyMsg ? "row-reverse" : "row",
      alignItems: "flex-end", gap: 8,
      marginTop: sameAuthorPrev ? 2 : 16,
      marginBottom: reaction ? 20 : 0,
      position: "relative",
      animation: "msg-in 0.2s cubic-bezier(.34,1.3,.64,1)",
    }}
      onMouseEnter={() => { setHovered(true); clearTimeout(timerRef.current); }}
      onMouseLeave={() => { setHovered(false); timerRef.current = setTimeout(() => setShowPicker(false), 300); }}
    >
      {!isMyMsg && (
        <div style={{ width: 30, flexShrink: 0, alignSelf: "flex-end" }}>
          {!sameAuthorNext
            ? <Avatar name={msg.admin || msg.user} size={30} online picUrl={userPicUrl} isAdmin={isAdminChat} />
            : <div style={{ width: 30 }} />}
        </div>
      )}
      {isMyMsg && (
        <div style={{ width: 30, flexShrink: 0, alignSelf: "flex-end" }}>
          {!sameAuthorNext
            ? <Avatar name={msg.admin || msg.user} size={30} picUrl={userPicUrl} isAdmin />
            : <div style={{ width: 30 }} />}
        </div>
      )}

      <div style={{ maxWidth: "64%", display: "flex", flexDirection: "column", alignItems: isMyMsg ? "flex-end" : "flex-start", position: "relative" }}>
        {isAdminChat && !isMyMsg && !sameAuthorPrev && (
          <div style={{ fontSize: 10, color: isDark ? "rgba(167,139,250,0.8)" : "#7c3aed", fontWeight: 700, marginBottom: 3, paddingLeft: 4 }}>
            {msg.admin || msg.user}
          </div>
        )}

        {isPinned && (
          <div style={{ fontSize: 10, color: "#f7971e", fontWeight: 700, marginBottom: 3, display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ animation: "wiggle 2s infinite" }}>📌</span> Pinned message
          </div>
        )}

        <div style={{ display: "flex", flexDirection: isMyMsg ? "row-reverse" : "row", alignItems: "center", gap: 6, position: "relative" }}>
          <div style={{ position: "relative" }}>
            {showPicker && (
              <ReactionPicker isAdmin={isMyMsg} currentReaction={reaction}
                onPick={(emoji) => onReact(msg.messageId, emoji)}
                onClose={() => setShowPicker(false)} isDark={isDark} />
            )}
          </div>

          <div style={{ position: "relative" }}>
            {isImage ? (
              <div onClick={() => window.open(imageUrl, "_blank")}
                style={{ borderRadius: 16, overflow: "hidden", maxWidth: 260, cursor: "pointer", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
                <img src={imageUrl} alt="shared" style={{ width: "100%", display: "block" }} />
              </div>
            ) : (
              <div style={{
                background: isMyMsg
                  ? "linear-gradient(135deg, #0084ff, #006fde)"
                  : (isAdminChat ? otherAdminBg : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)")),
                backdropFilter: !isMyMsg ? "blur(10px)" : "none",
                color: isMyMsg ? "#fff" : (isDark ? "#e8e8f0" : "#0f0f1a"),
                borderRadius: isMyMsg
                  ? `18px 4px ${sameAuthorNext ? "4px" : "18px"} 18px`
                  : `4px 18px 18px ${sameAuthorNext ? "4px" : "18px"}`,
                padding: hasReply ? "10px 15px 9px" : "10px 15px",
                fontSize: 14, lineHeight: 1.5, wordBreak: "break-word",
                boxShadow: isMyMsg
                  ? "0 4px 15px rgba(0,132,255,0.35)"
                  : (isAdminChat ? "0 4px 15px rgba(124,58,237,0.15)" : (isDark ? "0 2px 8px rgba(0,0,0,0.25)" : "0 2px 6px rgba(0,0,0,0.1)")),
                border: !isMyMsg
                  ? (isAdminChat
                    ? `1px solid ${isDark ? "rgba(124,58,237,0.3)" : "rgba(124,58,237,0.2)"}`
                    : `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`)
                  : "none",
                transition: "transform 0.1s",
                cursor: "default",
              }} onDoubleClick={() => onReply(msg)}>
                {hasReply && (
                  <ReplySnippet
                    quotedContent={displayQuote}
                    quotedSenderType={displayQuoteSender}
                    isAdmin={isMyMsg}
                    isDark={isDark}
                    activeUser={activeUser}
                  />
                )}
                {actualContent}
              </div>
            )}

            {reaction && (
              <div onClick={() => onReact(msg.messageId, reaction)}
                style={{
                  position: "absolute", bottom: -16,
                  [isMyMsg ? "left" : "right"]: 6,
                  background: isDark ? "rgba(20,20,35,0.95)" : "rgba(255,255,255,0.95)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                  borderRadius: 20, padding: "2px 8px 2px 5px",
                  fontSize: 13, boxShadow: "0 3px 12px rgba(0,0,0,0.2)",
                  zIndex: 5, cursor: "pointer", userSelect: "none",
                  display: "flex", alignItems: "center", gap: 4,
                  animation: "pop-spring 0.22s cubic-bezier(.34,1.56,.64,1)",
                }}>
                <span style={{ fontSize: 14 }}>{reaction}</span>
                <span style={{ fontSize: 11, color: isDark ? "#94a3b8" : "#65676b", fontWeight: 600 }}>1</span>
              </div>
            )}
          </div>

          {hovered && (
            <div style={{ display: "flex", flexDirection: isMyMsg ? "row-reverse" : "row", gap: 4, alignItems: "center", animation: "fade-in 0.1s ease" }}>
              {[
                {
                  icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M8 13s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>,
                  title: "React", action: () => setShowPicker(v => !v)
                },
                {
                  icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 00-4-4H4" /></svg>,
                  title: "Reply", action: () => onReply(msg)
                },
                {
                  icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2" /><polygon points="12 15 7 10 17 10" /></svg>,
                  title: isPinned ? "Unpin" : "Pin", action: () => onPin(msg.messageId), active: isPinned
                },
                ...(canDelete ? [{
                  icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" /></svg>,
                  title: "Delete", action: () => onDelete(msg.messageId), danger: true
                }] : []),
              ].map((btn) => (
                <button key={btn.title} onClick={btn.action} title={btn.title}
                  style={{
                    background: btn.danger
                      ? (isDark ? "rgba(239,68,68,0.75)" : "rgba(239,68,68,0.1)")
                      : btn.active ? "rgba(247,151,30,0.7)" : (isDark ? "rgba(255,255,255,0.88)" : "rgba(0,0,0,0.07)"),
                    border: btn.active ? "1px solid rgba(247,151,30,0.5)" : "1px solid transparent",
                    borderRadius: "50%", width: 30, height: 30,
                    cursor: "pointer", fontSize: 14,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: btn.danger ? "#ef4444" : btn.active ? "#f7971e" : (isDark ? "#1a1a2e" : "#374151"),
                    backdropFilter: "blur(10px)",
                    transition: "transform 0.12s, background 0.12s",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.18)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                >{btn.icon}</button>
              ))}
            </div>
          )}
        </div>

        {showTime && (
          <span style={{ fontSize: 10, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)", marginTop: reaction ? 20 : 5, padding: "0 6px" }}>
            {formatTime(msg.timestamp)}{isMyMsg && msg.read && " · Seen ✓"}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Admin Conversation Item ──────────────────────────────────
function AdminConvItem({ admin, active, onClick, isDark, picUrl, isOnline }) {
  const unread = admin.unreadCount > 0;
  return (
    <div onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 11,
      padding: "8px 12px", borderRadius: 14, cursor: "pointer",
      background: active ? (isDark ? "rgba(124,58,237,0.15)" : "rgba(124,58,237,0.1)") : "transparent",
      borderLeft: `3px solid ${active ? "#7c3aed" : "transparent"}`,
      transition: "all 0.15s",
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
      <Avatar name={admin.username} size={44} online={isOnline} picUrl={picUrl} isAdmin />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{
            fontWeight: unread ? 800 : 600, fontSize: 14,
            color: isDark ? "#f0f0f8" : "#0f0f1a",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 120,
          }}>{admin.fullNames || admin.username}</span>
          <span style={{ fontSize: 10, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)", flexShrink: 0, marginLeft: 4 }}>
            {admin.lastTimestamp ? formatDay(admin.lastTimestamp) : ""}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{
            fontSize: 11.5,
            color: isOnline ? "#31a24c" : (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)"),
            fontWeight: isOnline ? 600 : 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
          }}>
            {admin.lastMessage
              ? (admin.lastMessage.startsWith("[IMAGE]") ? "📷 Photo" : admin.lastMessage)
              : (isOnline ? "● Online" : "🔒 No messages yet")}
          </span>
          {unread && (
            <span style={{
              background: "linear-gradient(135deg,#7c3aed,#db2777)",
              color: "#fff", borderRadius: "50%",
              width: 20, height: 20, fontSize: 10, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginLeft: 6,
              boxShadow: "0 2px 8px rgba(124,58,237,0.5)",
              animation: "pop-spring 0.3s ease",
            }}>{admin.unreadCount > 9 ? "9+" : admin.unreadCount}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── User Conversation Item ───────────────────────────────────
function ConversationItem({ conv, active, onClick, isDark, picUrl }) {
  const unread = conv.unreadCount > 0;
  return (
    <div onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 11,
      padding: "8px 12px", borderRadius: 14, cursor: "pointer",
      background: active ? (isDark ? "rgba(0,132,255,0.15)" : "rgba(0,132,255,0.1)") : "transparent",
      borderLeft: `3px solid ${active ? "#0084ff" : "transparent"}`,
      transition: "all 0.15s",
    }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
      <Avatar name={conv.username} size={44} online={conv.online} picUrl={picUrl} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{
            fontWeight: unread ? 800 : 600, fontSize: 14,
            color: isDark ? "#f0f0f8" : "#0f0f1a",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130,
          }}>{conv.displayName || conv.username}</span>
          <span style={{ fontSize: 10, color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.35)", flexShrink: 0, marginLeft: 4 }}>
            {conv.lastTimestamp ? formatDay(conv.lastTimestamp) : ""}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{
            fontSize: 12,
            color: unread ? (isDark ? "#c0c0d8" : "#0f0f1a") : (isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.45)"),
            fontWeight: unread ? 600 : 400,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
          }}>
            {conv.lastMessage?.startsWith("[IMAGE]") ? "📷 Photo" : (conv.lastMessage ?? "No messages yet")}
          </span>
          {unread && (
            <span style={{
              background: "linear-gradient(135deg,#0084ff,#006fde)",
              color: "#fff", borderRadius: "50%",
              width: 20, height: 20, fontSize: 10, fontWeight: 700,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, marginLeft: 6,
              boxShadow: "0 2px 8px rgba(0,132,255,0.5)",
              animation: "pop-spring 0.3s ease",
            }}>{conv.unreadCount > 9 ? "9+" : conv.unreadCount}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Forward Modal ────────────────────────────────────────────
function ForwardModal({ username, currentAssignee, onForward, onClose, isDark }) {
  const [targetAdmin, setTargetAdmin] = useState("");
  const [forwarding, setForwarding] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [search, setSearch] = useState("");

  const isDarkBg = isDark ? "#0d1117" : "#ffffff";
  const borderC = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  useEffect(() => {
    fetch(`${api}/Admin`, { headers: { accept: "text/plain" } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAdmins(data.filter(a => a.username?.toLowerCase() !== "superadmin" && a.isApproved !== false)))
      .catch(() => { })
      .finally(() => setLoadingAdmins(false));
  }, []);

  const COLORS = ["#0084ff", "#00b09b", "#f7971e", "#e040fb", "#f44336", "#43a047", "#ff5722", "#3f51b5"];
  const filtered = admins.filter(a =>
    a.username?.toLowerCase().includes(search.toLowerCase()) ||
    a.fullNames?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 99999, animation: "fade-in 0.2s ease" }}>
      <div style={{ background: isDarkBg, border: `1px solid ${borderC}`, borderRadius: 24, width: 460, maxHeight: "88vh", boxShadow: "0 30px 80px rgba(0,0,0,0.6)", overflow: "hidden", display: "flex", flexDirection: "column", animation: "slide-up 0.25s cubic-bezier(.34,1.2,.64,1)" }}>
        <div style={{ padding: "22px 24px 18px", background: "linear-gradient(135deg, #0084ff 0%, #8b5cf6 100%)", position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>⚡</div>
            <div>
              <h3 style={{ margin: 0, color: "#fff", fontSize: 17, fontWeight: 800 }}>Forward Conversation</h3>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.75)", fontSize: 12 }}>Assign to another admin</p>
            </div>
          </div>
          <button onClick={onClose} style={{ position: "absolute", top: 18, right: 18, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", color: "#fff", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ padding: "16px 20px", flex: 1, overflowY: "auto" }}>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, opacity: 0.5 }}>🔍</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search admin..." autoFocus
              style={{ width: "100%", boxSizing: "border-box", background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", border: `1.5px solid ${borderC}`, borderRadius: 12, padding: "10px 14px 10px 36px", fontSize: 13, color: isDark ? "#f0f0f8" : "#0f0f1a", outline: "none", fontFamily: "inherit" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 270, overflowY: "auto" }}>
            {loadingAdmins ? (
              <div style={{ textAlign: "center", padding: 24, color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)", fontSize: 13 }}>Loading admins...</div>
            ) : filtered.map(admin => {
              const isSelected = targetAdmin === admin.username;
              const isCurrent = admin.username === currentAssignee;
              const aColor = COLORS[admin.username?.charCodeAt(0) % COLORS.length] ?? "#0084ff";
              return (
                <div key={admin.username} onClick={() => !isCurrent && setTargetAdmin(admin.username)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 12, border: `1.5px solid ${isSelected ? "#0084ff" : borderC}`, background: isSelected ? (isDark ? "rgba(0,132,255,0.12)" : "rgba(0,132,255,0.07)") : "transparent", cursor: isCurrent ? "default" : "pointer", opacity: isCurrent ? 0.55 : 1, transition: "all 0.15s" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: `linear-gradient(135deg, ${aColor}, ${aColor}99)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "#fff", flexShrink: 0, border: isSelected ? `2px solid #0084ff` : "none" }}>
                    {(admin.fullNames?.[0] || admin.username?.[0] || "A")?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? "#0084ff" : (isDark ? "#f0f0f8" : "#0f0f1a") }}>{admin.fullNames || admin.username}</div>
                    <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)" }}>@{admin.username}</div>
                  </div>
                  {isCurrent && <span style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700 }}>Current</span>}
                  {isSelected && !isCurrent && <span style={{ background: "rgba(0,132,255,0.15)", color: "#0084ff", borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700 }}>✓ Selected</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${borderC}`, display: "flex", gap: 10 }}>
          <button onClick={async () => { if (!targetAdmin) return; setForwarding(true); await onForward(targetAdmin); setForwarding(false); }}
            disabled={!targetAdmin || forwarding}
            style={{ flex: 1, background: targetAdmin && !forwarding ? "linear-gradient(135deg,#0084ff,#006fde)" : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"), color: targetAdmin && !forwarding ? "#fff" : (isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)"), border: "none", borderRadius: 14, padding: "13px 18px", fontSize: 14, fontWeight: 700, cursor: targetAdmin && !forwarding ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {forwarding ? "Forwarding..." : "⚡ Forward & Assign"}
          </button>
          <button onClick={onClose} style={{ background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)", color: isDark ? "#f0f0f8" : "#0f0f1a", border: "none", borderRadius: 14, padding: "13px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function AdminMessenger({ adminUsername: _adminUsername, adminRole: _adminRole, initialAdminChat, initialTab = "users", initialUser, }) {
  const adminData = (() => { try { return JSON.parse(localStorage.getItem("admin") || "{}"); } catch { return {}; } })();

  const rawUsername = _adminUsername || adminData?.username || adminData?.claimUsername || adminData?.identificationCode || null; const adminUsername = rawUsername ?? null;
  const adminRole = _adminRole || localStorage.getItem("userRole") || adminData?.role || "admin";
  const isSuperAdmin = adminRole?.toLowerCase() === "superadmin";
  useEffect(() => {
    if (initialTab === "admins" && initialAdminChat) {
      setChatMode("admins");
      // wait for adminList to load, then selectAdminChat
      const t = setTimeout(() => selectAdminChat(initialAdminChat), 1200);
      return () => clearTimeout(t);
    } else if (initialUser) {
      selectConversation(initialUser);
    }
  }, []);
  const [chatMode, setChatMode] = useState("users");
  const [isDark, setIsDark] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [localReactions, setLocalReactions] = useState({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [search, setSearch] = useState("");
  const [newUserInput, setNewUserInput] = useState("");
  const [showNewConvo, setShowNewConvo] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinnedMessageId, setPinnedMessageId] = useState(null);
  const [claimLoading, setClaimLoading] = useState(true);
  const [claimStatus, setClaimStatus] = useState({ assigned: false, assignedTo: null });
  const [userPics, setUserPics] = useState({});
  const [adminPic, setAdminPic] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [adminOnline, setAdminOnline] = useState({});
  const [adminConvos, setAdminConvos] = useState([]);
  const [activeAdminChat, setActiveAdminChat] = useState(null);

  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  // ── These are needed early for handleDelete / handleReact ──
  // Defined as derived values after state declarations
  const isAdminChatMode = chatMode === "admins";

  const loadedPicsRef = useRef({});
  const loadPic = useCallback(async (username) => {
    if (!username || loadedPicsRef.current[username] !== undefined) return;
    loadedPicsRef.current[username] = null;
    const url = await fetchProfilePic(username);
    loadedPicsRef.current[username] = url;

    if (url) {
      // Also store under all key variants so any lookup finds it
      const clean = username.replace(/^admin_/i, "");
      const prefixed = `admin_${clean}`;
      loadedPicsRef.current[clean] = url;
      loadedPicsRef.current[prefixed] = url;
      setUserPics(prev => ({
        ...prev,
        [username]: url,
        [clean]: url,
        [prefixed]: url,
      }));
    }
  }, []);
  useEffect(() => {
    const username = adminData?.username || adminUsername;
    if (!username) return;

    const fetchAvatar = async () => {
      const clean = username.replace(/^admin_/i, "");
      const prefixed = `admin_${clean}`;
      const endpoints = [
        `${api}/Admin/get-profile-picture/${encodeURIComponent(username)}`,
        `${api}/Admin/get-profile-picture/${encodeURIComponent(prefixed)}`,
        `${api}/Admin/get-profile-picture/${encodeURIComponent(clean)}`,
      ];

      for (const url of endpoints) {
        try {
          const res = await fetch(url);
          if (res.ok && res.status !== 204) {
            const blob = await res.blob();
            const objUrl = URL.createObjectURL(blob);
            setAdminPic(objUrl);
            return;
          }
        } catch { }
      }
    };

    fetchAvatar();
  }, [adminUsername, adminData?.username]);


  const th = {
    bg: isDark ? "#0d1117" : "#f5f7fb",
    sidebar: isDark ? "#0f1117" : "#ffffff",
    border: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)",
    header: isDark ? "#0f1117" : "#ffffff",
    input: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
    text: isDark ? "#e8e8f0" : "#0f0f1a",
    sub: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.4)",
  };

  const normalizeUsername = u => (u || "").toLowerCase().trim().replace(/^admin_/, "").replace(/\s+/g, "");
  const usernamesMatch = (a, b) => normalizeUsername(a) === normalizeUsername(b);

  const isViewer = (() => {
    if (isSuperAdmin || isAdminChatMode) return false;
    if (claimLoading) return false;
    if (!claimStatus.assigned) return false;
    const mine = (
      usernamesMatch(claimStatus.assignedTo, adminUsername) ||
      usernamesMatch(claimStatus.assignedTo, adminData?.username) ||
      usernamesMatch(claimStatus.assignedTo, `admin_${adminUsername}`) ||
      usernamesMatch(`admin_${claimStatus.assignedTo}`, adminUsername) ||
      usernamesMatch(`admin_${claimStatus.assignedTo}`, adminData?.username)
    );
    return !mine;
  })();

  const isMyClaim = isSuperAdmin || !claimStatus.assigned || (
    claimStatus.assigned && (
      usernamesMatch(claimStatus.assignedTo, adminData?.username) ||
      usernamesMatch(claimStatus.assignedTo, adminUsername) ||
      usernamesMatch(claimStatus.assignedTo, `admin_${adminUsername}`) ||
      usernamesMatch(`admin_${claimStatus.assignedTo}`, adminUsername)
    )
  ); const canSend = isAdminChatMode
    ? !!activeAdminChat
    : (isSuperAdmin || isMyClaim || !claimStatus.assigned);
  // ── Fetch admin list + online status ──────────────────────────
  const fetchAdminList = useCallback(async () => {
    try {
      const res = await fetch(`${api}/Admin`, { headers: { accept: "text/plain" } });
      if (!res.ok) return;
      const data = await res.json();
      const rawMe = adminData?.username || adminUsername || "";
      const cleanMe = rawMe.replace(/^admin_/i, "").toLowerCase();
      const filtered = data.filter(a => {
        const cleanA = (a.username || "").replace(/^admin_/i, "").toLowerCase();
        return (
          cleanA !== "superadmin" &&
          cleanA !== cleanMe &&
          a.isApproved !== false
        );
      });
      setAdminList(filtered);

      try {
        const onlineRes = await fetch(`${api}/ChatManagement/online-status`);
        if (onlineRes.ok) {
          const onlineData = await onlineRes.json();
          const map = {};
          onlineData.forEach(a => { map[a.adminUsername] = a.isOnline; });
          setAdminOnline(map);
        } else {
          const map = {};
          filtered.forEach(a => { map[a.username] = false; });
          setAdminOnline(map);
        }
      } catch {
        const map = {};
        filtered.forEach(a => { map[a.username] = false; });
        setAdminOnline(map);
      }

      filtered.forEach(a => {
        const clean = (a.username || "").replace(/^admin_/i, "");
        loadPic(clean);
        loadPic(`admin_${clean}`);
        loadPic(a.username);
      });
    } catch (e) { console.error(e); }
  }, [adminUsername, loadPic]);

  // ── Fetch admin DM thread ──────────────────────────────────────
  const fetchAdminMessages = useCallback(async (targetAdmin) => {
    const rawMe = adminData?.username || adminUsername || "";
    const callerAdmin = rawMe.startsWith("admin_") ? rawMe : `admin_${rawMe.replace(/^admin_/i, "")}`;

    if (!targetAdmin || !callerAdmin) return;
    try {
      const res = await fetch(
        `${api}/AdminDm/thread/${encodeURIComponent(callerAdmin)}/${encodeURIComponent(targetAdmin)}`
      );
      if (!res.ok) return;
      const raw = await res.json();
      const msgs = Array.isArray(raw) ? raw : (raw.data ?? []);

      const mapped = msgs.map(m => {
        const dmId = m.dmId ?? m.DmId;
        const senderAdmin = m.senderAdmin ?? m.SenderAdmin ?? "";
        const content = m.content ?? m.Content ?? "";
        const fileData = m.fileData ?? m.FileData ?? null;
        const sentAt = m.sentAt ?? m.SentAt ?? m.createdAt ?? m.CreatedAt;
        const reactionType = m.reactionType ?? m.ReactionType ?? null;
        const isRead = m.read ?? m.Read ?? m.isRead ?? m.IsRead ?? false;
        const replyToDmId = m.replyToDmId ?? m.ReplyToDmId ?? null;
        const replyToContent = m.replyToContent ?? m.ReplyToContent ?? null;
        const replyToSender = m.replyToSender ?? m.ReplyToSender ?? null;
        const fileName = m.fileName ?? m.FileName ?? null;
        const fileType = m.fileType ?? m.FileType ?? null;

        const finalContent = fileData
          ? `[IMAGE]${fileData}`
          : content;

        return {
          messageId: dmId,
          admin: senderAdmin,
          user: m.recipientAdmin ?? m.RecipientAdmin ?? "",
          content: finalContent,
          timestamp: sentAt,
          senderType: "admin",
          reactionType,
          read: isRead,
          replyToDmId,
          replyToContent,
          replyToSender,
          fileName,
          fileType,
        };
      });

      setMessages(mapped);
    } catch (e) { console.error(e); }
  }, [adminUsername]);

  // ── Mark admin DM messages as read ────────────────────────────
  const markAdminDmRead = useCallback(async (targetAdmin) => {
    if (!targetAdmin || !adminUsername) return;
    try {
      await fetch(
        `${api}/AdminDm/markRead/${encodeURIComponent(adminUsername)}/${encodeURIComponent(targetAdmin)}`,
        { method: "POST" }
      );
    } catch { }
  }, [adminUsername]);

  // ── Build admin conversation list ──────────────────────────────
  const fetchAdminConvos = useCallback(async () => {
    if (adminList.length === 0) return;

    let summaries = [];
    try {
      if (adminUsername) {
        const res = await fetch(`${api}/AdminDm/contacts/${encodeURIComponent(adminUsername)}`);
        if (res.ok) {
          const raw = await res.json();
          summaries = Array.isArray(raw) ? raw : (raw.data ?? []);
        }
      }
    } catch (e) { console.error(e); }

    const existingUsernames = new Set(summaries.map(s => (s.otherAdmin ?? "").toLowerCase()));
    const cleanMe = (adminUsername || "").replace(/^admin_/i, "").toLowerCase();
    const noHistoryAdmins = adminList
      .filter(a => {
        const cleanA = (a.username || "").replace(/^admin_/i, "").toLowerCase();
        return !existingUsernames.has(cleanA) && cleanA !== cleanMe;
      })
      .map(a => ({
        username: a.username, fullNames: a.fullNames,
        lastMessage: null, lastTimestamp: null, unreadCount: 0, hasHistory: false,
      }));

    const fromApi = summaries
      .filter(s => {
        const cleanA = (s.otherAdmin || "").replace(/^admin_/i, "").toLowerCase();
        return cleanA !== cleanMe && cleanA !== "superadmin";
      })
      .map(s => ({
        username: s.otherAdmin,
        fullNames: s.fullNames ?? null,
        username: s.otherAdmin,
        fullNames: s.fullNames ?? null,
        lastMessage: s.lastMessage ?? null,
        lastTimestamp: s.lastTimestamp ?? null,
        unreadCount: s.unreadCount ?? 0,
        hasHistory: true,
      }));

    const adminMap = {};
    adminList.forEach(a => { adminMap[a.username?.toLowerCase()] = a.fullNames; });
    fromApi.forEach(c => {
      if (!c.fullNames) c.fullNames = adminMap[c.username?.toLowerCase()] ?? null;
    });

    const all = [...fromApi, ...noHistoryAdmins];
    all.sort((a, b) => {
      if (a.hasHistory && !b.hasHistory) return -1;
      if (!a.hasHistory && b.hasHistory) return 1;
      if (!a.lastTimestamp && !b.lastTimestamp) return 0;
      if (!a.lastTimestamp) return 1;
      if (!b.lastTimestamp) return -1;
      return new Date(b.lastTimestamp) - new Date(a.lastTimestamp);
    });
    setAdminConvos(all);
  }, [adminUsername, adminList]);

  // ── Send admin DM ──────────────────────────────────────────────
  const sendAdminMessage = async () => {
    const text = input.trim();
    if (!text || !activeAdminChat || sending) return;
    setSending(true);
    try {
      const rawSender = adminData?.username || adminUsername || "";
      const cleanSender = rawSender.replace(/^admin_/i, "");
      const senderAdmin = cleanSender ? `admin_${cleanSender}` : "admin";
      const body = {
        senderAdmin: senderAdmin,
        recipientAdmin: activeAdminChat,
        content: text,
        fileData: null,
        fileName: null,
        fileType: null,
        fileSize: null,
        // FIX: pass replyToDmId so backend can store the reply reference
        replyToDmId: replyTo?.messageId ?? null,
      };
      const res = await fetch(`${api}/AdminDm/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setInput("");
        setReplyTo(null);
        await fetchAdminMessages(activeAdminChat);
        await fetchAdminConvos();
      }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const selectAdminChat = useCallback(async (targetAdmin) => {
    setActiveAdminChat(targetAdmin);
    setActiveUser(null);
    setReplyTo(null);
    setMessages([]);
    setLoading(true);
    const clean = (targetAdmin || "").replace(/^admin_/i, "");
    loadPic(clean);
    loadPic(`admin_${clean}`);
    loadPic(targetAdmin);
    await fetchAdminMessages(targetAdmin);
    // FIX: clear unread badge when opening the thread
    await markAdminDmRead(targetAdmin);
    setAdminConvos(prev => prev.map(c => c.username === targetAdmin ? { ...c, unreadCount: 0 } : c));
    setLoading(false);
  }, [fetchAdminMessages, markAdminDmRead, loadPic]);

  const fetchClaim = useCallback(async (username) => {
    try {
      setClaimLoading(true);
      const res = await fetch(`${chatMgmt}/ChatManagement/claim/${encodeURIComponent(username)}`);
      if (!res.ok) return;
      const data = await res.json();
      setClaimStatus({ assigned: !!data.assigned, assignedTo: data.assignedTo ?? null });
      if (data.assignedTo) {
        const cleanName = data.assignedTo.replace(/^admin_/i, "");
        const prefixedName = `admin_${cleanName}`;

        // Fetch using prefixed name (admin_Peter) — that's what the API expects
        let pic = null;
        try {
          const res = await fetch(`${api}/Admin/get-profile-picture/${encodeURIComponent(prefixedName)}`);
          if (res.ok) {
            const blob = await res.blob();
            pic = URL.createObjectURL(blob);
          }
        } catch { }

        // Fallback: try clean name
        if (!pic) {
          try {
            const res = await fetch(`${api}/Admin/get-profile-picture/${encodeURIComponent(cleanName)}`);
            if (res.ok) {
              const blob = await res.blob();
              pic = URL.createObjectURL(blob);
            }
          } catch { }
        }

        if (pic) {
          PIC_CACHE[cleanName] = pic;
          PIC_CACHE[prefixedName] = pic;
          loadedPicsRef.current[cleanName] = pic;
          loadedPicsRef.current[prefixedName] = pic;
          setUserPics(prev => ({
            ...prev,
            [cleanName]: pic,
            [prefixedName]: pic,
          }));
        }
      }
    } catch (e) { console.error(e); } finally { setClaimLoading(false); }
  }, [loadPic]);

  const fetchMessages = useCallback(async (username) => {
    if (!username) return;
    try {
      // /Messages/user/{username} returns ALL messages for this user regardless of Admin column
      const res = await fetch(`${api}/Messages/user/${encodeURIComponent(username)}`);
      if (!res.ok) return;
      const json = await res.json();
      const data = Array.isArray(json) ? json : (json.data ?? []);

      const mapped = data.map(m => ({
        messageId: m.MessageId ?? m.messageId,
        user: m.User ?? m.user ?? "",
        admin: m.Admin ?? m.admin ?? "",
        content: m.Content ?? m.content ?? "",
        timestamp: m.Timestamp ?? m.timestamp ?? m.CreatedDate ?? m.createdDate,
        senderType: m.SenderType ?? m.senderType ?? "user",
        read: m.Read ?? m.read ?? false,
        seen: m.Seen ?? m.seen ?? false,
        likes: m.Likes ?? m.likes ?? 0,
        fileData: m.FileData ?? m.fileData ?? null,
        fileName: m.FileName ?? m.fileName ?? null,
        fileType: m.FileType ?? m.fileType ?? null,
        reactionType: m.ReactionType ?? m.reactionType ?? null,
      }));
      mapped.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMessages(mapped);
    } catch (e) { console.error("fetchMessages error:", e); }
  }, [adminUsername]);

  const fetchConversations = useCallback(async () => {
    try {
      console.log("🔥 fetchConversations RUNNING, adminUsername:", adminUsername);
      // Fetch all users AND all messages in parallel
      const [usersRes, msgsRes] = await Promise.all([
        fetch(`${api}/Users`),
        fetch(`${api}/Messages/all`),
      ]);

      const usersJson = usersRes.ok ? await usersRes.json() : [];
      const msgsJson = msgsRes.ok ? await msgsRes.json() : [];

      const users = Array.isArray(usersJson) ? usersJson : (usersJson.data ?? []);
      const allMsgs = Array.isArray(msgsJson) ? msgsJson : (msgsJson.data ?? []);

      // Save ALL users (role=user only) for the sidebar
      const regularUsers = users.filter(u => { const r = (u.role ?? "").toLowerCase(); return !r.includes("admin") && !r.includes("superadmin"); });
      setAllUsers(regularUsers);
      regularUsers.forEach(u => loadPic(u.username ?? u.email));

      // Build conversations — users who have messages
      const list = regularUsers.map(u => {
        const userMsgs = allMsgs.filter(m => {
          const msgUser = m.User ?? m.user ?? "";
          return msgUser === u.email || msgUser === u.username;
        });
        if (userMsgs.length === 0) return null;
        const sorted = [...userMsgs].sort((a, b) =>
          new Date(b.Timestamp ?? b.timestamp) - new Date(a.Timestamp ?? a.timestamp)
        );
        const lastMsg = sorted[0];
        const unreadCount = sorted.filter(m =>
          (m.SenderType ?? m.senderType) === "user" && !(m.Read ?? m.read)
        ).length;
        return {
          username: u.email ?? u.username,
          displayName: u.fullNames || u.username || u.email,
          lastMessage: lastMsg?.Content ?? lastMsg?.content ?? null,
          lastTimestamp: lastMsg?.Timestamp ?? lastMsg?.timestamp ?? null,
          unreadCount,
          online: true,
        };
      }).filter(Boolean);

      list.sort((a, b) => new Date(b.lastTimestamp) - new Date(a.lastTimestamp));
      setConversations(list);
    } catch (e) { console.error("fetchConversations error:", e); }
  }, [adminUsername, loadPic]);

  const fetchPin = useCallback(async (username) => {
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/pin/${encodeURIComponent(username)}`);
      if (!res.ok) return;
      const data = await res.json();
      setPinnedMessageId(data.pinned ? data.messageId : null);
    } catch { }
  }, []);

  const markRead = useCallback(async (username) => {
    try {
      await fetch(`${api}/Messages/markAllAsRead/${encodeURIComponent(username)}`, { method: "POST" });
    } catch { }
  }, []);

  const selectConversation = useCallback(async (username) => {
    setActiveUser(username);
    setActiveAdminChat(null);
    setReplyTo(null);
    setLoading(true);
    setPinnedMessageId(null);
    setClaimStatus({ assigned: false, assignedTo: null });
    setClaimLoading(true);
    loadPic(username);
    await Promise.all([fetchClaim(username), fetchMessages(username), fetchPin(username), markRead(username)]);
    setLoading(false);
    setConversations(prev => prev.map(c => c.username === username ? { ...c, unreadCount: 0 } : c));
  }, [fetchClaim, fetchMessages, fetchPin, markRead, loadPic]);

  // ── Polling ────────────────────────────────────────────────────
  useEffect(() => {
    fetchConversations();
    fetchAdminList();
    pollRef.current = setInterval(() => {
      fetchConversations();
      fetchAdminList();
      if (activeUser) { fetchMessages(activeUser); fetchClaim(activeUser); }
      // FIX: also poll admin convos list and active admin DM thread
      if (activeAdminChat) fetchAdminMessages(activeAdminChat);
      fetchAdminConvos();
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchConversations, fetchMessages, fetchClaim, fetchAdminList, fetchAdminMessages, fetchAdminConvos, activeUser, activeAdminChat]);

  useEffect(() => {
    if (adminList.length > 0) fetchAdminConvos();
  }, [adminList, fetchAdminConvos]);
  // Sa message bubble ng admin, ganitong fetch:
  // BAGO — idagdag ito pagkatapos ng:  const [adminPic, setAdminPic] = useState(null);
  const resolvePic = useCallback((username) => {
    if (!username) return null;
    const clean = (username || "").replace(/^admin_/i, "");
    const prefixed = `admin_${clean}`;
    return (
      userPics[prefixed] ?? userPics[clean] ?? userPics[username] ??
      PIC_CACHE[prefixed] ?? PIC_CACHE[clean] ?? PIC_CACHE[username] ?? null
    );
  }, [userPics]);
  // Heartbeat removed — endpoint not available

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { if (replyTo) inputRef.current?.focus(); }, [replyTo]);

  // ── Claim ──────────────────────────────────────────────────────
  const claimConversation = async () => {
    if (!activeUser || claiming) return;
    const resolvedAdmin = adminData?.username || adminUsername;
    if (!resolvedAdmin) return;
    setClaiming(true);
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: activeUser, adminUsername: resolvedAdmin, role: adminRole }),
      });
      const data = await res.json();
      if (res.ok) setClaimStatus({ assigned: true, assignedTo: data.assignedTo ?? resolvedAdmin });
      else { alert(`⚠️ ${data.message}`); await fetchClaim(activeUser); }
    } catch (e) { console.error(e); } finally { setClaiming(false); }
  };

  const handleForward = async (targetAdmin) => {
    if (!targetAdmin || !activeUser) return;
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/claim`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: activeUser, adminUsername: targetAdmin, role: "superadmin" }),
      });
      if (res.ok) {
        const data = await res.json();
        setClaimStatus({ assigned: true, assignedTo: data.assignedTo ?? targetAdmin });
        setShowForwardModal(false);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`❌ ${err.message ?? "Forward failed"}`);
      }
    } catch (e) { console.error(e); }
  };

  const handleUnassign = async () => {
    if (!activeUser) return;
    try {
      const res = await fetch(
        `${chatMgmt}/ChatManagement/claim/${encodeURIComponent(activeUser)}?role=superadmin`,
        { method: "DELETE" }
      );
      if (res.ok) setClaimStatus({ assigned: false, assignedTo: null });
    } catch (e) { console.error(e); }
  };

  const handlePin = async (messageId) => {
    if (pinLoading) return;
    setPinLoading(true);
    const isUnpin = String(pinnedMessageId) === String(messageId);
    const target = activeUser || activeAdminChat;
    try {
      const res = await fetch(`${chatMgmt}/ChatManagement/pin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: target,
          messageId: isUnpin ? null : String(messageId),
          pinnedBy: adminUsername,
          unpin: isUnpin,
        }),
      });
      if (res.ok) setPinnedMessageId(isUnpin ? null : messageId);
    } catch (e) { console.error(e); } finally { setPinLoading(false); }
  };

  // FIX: isAdminChatMode is now declared at the top of the component
  // so handleDelete and handleReact can safely use it
  const handleDelete = async (messageId) => {
    try {
      if (isAdminChatMode) {
        await fetch(
          `${api}/AdminDm/${messageId}?callerAdmin=${encodeURIComponent(adminUsername)}`,
          { method: "DELETE" }
        );
      } else {
        await fetch(`${api}/Messages/${messageId}`, { method: "DELETE" });
      }
      setMessages(prev => prev.filter(m => m.messageId !== messageId));
    } catch (e) { console.error(e); }
  };

  const handleReact = async (messageId, emoji) => {
    setLocalReactions(prev => ({ ...prev, [messageId]: prev[messageId] === emoji ? null : emoji }));
    try {
      if (isAdminChatMode) {
        await fetch(
          `${api}/AdminDm/react/${messageId}?callerAdmin=${encodeURIComponent(adminUsername)}`,
          { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reactionType: emoji }) }
        );
      } else {
        await fetch(
          `${api}/Messages/react/${messageId}`,
          { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reactionType: emoji }) }
        );
      }
    } catch (e) { console.error(e); }
  };

  const sendMessage = async () => {
    if (chatMode === "admins") return sendAdminMessage();
    const text = input.trim();
    if (!text || !activeUser || sending || !canSend) return;
    setSending(true);

    let finalContent = text;
    if (replyTo) {
      const q = replyTo.content?.startsWith("[IMAGE]") ? "📷 Photo" : replyTo.content?.slice(0, 60);
      finalContent = `↩ "${q}" — ${text}`;
    }

    try {
      const res = await fetch(`${api}/Messages/sendAdmin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: activeUser,
          admin: claimStatus.assignedTo ?? adminUsername ?? "admin",
          content: finalContent
        }),
      });
      if (res.ok) {
        setInput("");
        setReplyTo(null);
        await fetchMessages(activeUser);
        await fetchConversations();
      }
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const sendImage = async () => {
    const target = activeUser || activeAdminChat;
    if (!imageFile || !target || !canSend) return;
    setUploadingImage(true);
    try {
      const base64 = await new Promise(resolve => {
        const r = new FileReader();
        r.onload = ev => resolve(ev.target.result);
        r.readAsDataURL(imageFile);
      });

      const fd = new FormData();
      fd.append("file", imageFile);
      let fileName = imageFile.name;
      try {
        const uploadRes = await fetch(`${api}/Messages/uploadFile`, { method: "POST", body: fd });
        if (uploadRes.ok) { const d = await uploadRes.json(); fileName = d.fileName ?? fileName; }
      } catch { }

      if (chatMode === "admins") {
        await fetch(`${api}/AdminDm/send`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            senderAdmin: (() => {
              const rawSender = adminData?.username || adminUsername || "";
              const cleanSender = rawSender.replace(/^admin_/i, "");
              return cleanSender ? `admin_${cleanSender}` : "admin";
            })(),


            recipientAdmin: activeAdminChat,
            content: null,
            fileData: base64,
            fileName,
            fileType: imageFile.type,
            fileSize: imageFile.size,
            replyToDmId: null,
          }),
        });
        setImageFile(null);
        await fetchAdminMessages(activeAdminChat);
        await fetchAdminConvos();
      } else {
        await fetch(`${api}/Messages/sendAdmin`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user: activeUser, admin: adminUsername,
            content: `[IMAGE]${base64}`, senderType: "admin",
            fileName, fileType: imageFile.type,
            fileData: base64.split(",")[1], fileSize: imageFile.size,
          }),
        });
        setImageFile(null);
        await fetchMessages(activeUser);
        await fetchConversations();
      }
    } catch (err) { console.error(err); } finally { setUploadingImage(false); }
  };

  const startNewConversation = () => {
    const username = newUserInput.trim();
    if (!username) return;
    if (!conversations.find(c => c.username === username))
      setConversations(prev => [{
        username, displayName: username,
        lastMessage: null, lastTimestamp: null, unreadCount: 0, online: false,
      }, ...prev]);
    setActiveUser(username);
    setActiveAdminChat(null);
    setMessages([]);
    setNewUserInput("");
    setShowNewConvo(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (imageFile) sendImage(); else sendMessage(); }
    if (e.key === "Escape") setReplyTo(null);
  };

  const filteredConvos = conversations.filter(c =>
    (c.displayName || c.username).toLowerCase().includes(search.toLowerCase())
  );

  const displayAdmins = adminConvos.length > 0
    ? adminConvos
    : adminList.map(a => ({
      username: a.username, fullNames: a.fullNames,
      lastMessage: null, lastTimestamp: null, unreadCount: 0, hasHistory: false,
    }));
  const filteredAdmins = displayAdmins.filter(a =>
    (a.fullNames || a.username || "").toLowerCase().includes(search.toLowerCase())
  );

  // FIX: canSendInput logic corrected — true when there's content AND able to send
  const canSendInput = !!(input.trim() || imageFile) && !sending && !uploadingImage && canSend;
  const pinnedMsg = pinnedMessageId ? messages.find(m => String(m.messageId) === String(pinnedMessageId)) : null;
  const mergedMessages = messages.map(m => ({
    ...m,
    reaction: localReactions[m.messageId] !== undefined
      ? localReactions[m.messageId]
      : m.reactionType ?? (m.likes ? "❤️" : null),
  }));

  const activeTarget = isAdminChatMode ? activeAdminChat : activeUser;
  const activeTargetInfo = isAdminChatMode
    ? adminList.find(a => a.username === activeAdminChat)
    : conversations.find(c => c.username === activeUser);

  // FIX: determine if a message was sent by the currently logged-in admin
  const isMyMessage = (msg) => {
    if (!isAdminChatMode) {
      return (msg.senderType ?? "").toLowerCase() === "admin";
    }
    const msgAdmin = (msg.admin || "").replace(/^admin_/i, "").toLowerCase();
    // Use adminData?.username as primary — it's the actual username, not fullname
    const rawMe = adminData?.username || adminUsername || "";
    const me = rawMe.replace(/^admin_/i, "").toLowerCase();
    return msgAdmin === me;
  };
  const onlineCount = Object.values(adminOnline).filter(Boolean).length;

  return (
    <div style={{
      display: "flex", height: "100%",
      fontFamily: "'SF Pro Text', -apple-system, 'Segoe UI', sans-serif",
      background: th.bg, overflow: "hidden",
    }}>
      <style>{`
        @keyframes spin        { to { transform: rotate(360deg); } }
        @keyframes fade-in     { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slide-up    { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slide-in    { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes msg-in      { from { opacity: 0; transform: scale(0.92) translateY(6px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes pop-spring  { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes wiggle      { 0%,100%{transform:rotate(0)}30%{transform:rotate(-12deg)}60%{transform:rotate(10deg)} }
        @keyframes pulse-green { 0%,100%{box-shadow:0 0 0 0 rgba(49,164,76,0.5)}70%{box-shadow:0 0 0 5px rgba(49,164,76,0)} }
        @keyframes pulse-blue  { 0%,100%{box-shadow:0 0 0 0 rgba(0,132,255,0.4)}70%{box-shadow:0 0 0 8px rgba(0,132,255,0)} }
        @keyframes pulse-purple{ 0%,100%{box-shadow:0 0 0 0 rgba(124,58,237,0.4)}70%{box-shadow:0 0 0 8px rgba(124,58,237,0)} }
        @keyframes glow-bounce { 0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-8px) scale(1.06)} }
        @keyframes typing-dot  { 0%,80%,100%{transform:scale(0.6);opacity:0.4}40%{transform:scale(1);opacity:1} }
        ::-webkit-scrollbar      { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>

      {showForwardModal && (
        <ForwardModal username={activeUser} currentAssignee={claimStatus.assignedTo}
          onForward={handleForward} onClose={() => setShowForwardModal(false)} isDark={isDark} />
      )}

      {/* ══════════════ SIDEBAR ══════════════ */}
      <div style={{ width: 280, display: "flex", flexDirection: "column", background: th.sidebar, borderRight: `1px solid ${th.border}`, flexShrink: 0 }}>
        <div style={{ padding: "18px 16px 12px", background: th.sidebar, borderBottom: `1px solid ${th.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ position: "relative", flexShrink: 0 }}>
                {adminPic ? (
                  <img
                    src={adminPic}
                    alt={adminUsername}
                    onError={() => setAdminPic(null)}
                    style={{
                      width: 40, height: 40, borderRadius: "50%",
                      objectFit: "cover", display: "block",
                      border: "2.5px solid rgba(255,255,255,0.15)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                    }}
                  />
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: "linear-gradient(135deg,#7c3aed,#db2777)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontWeight: 800, fontSize: 16,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                    border: "2.5px solid rgba(255,255,255,0.1)",
                  }}>
                    {(adminData?.fullNames?.[0] || adminUsername?.[0] || "A").toUpperCase()}
                  </div>
                )}
                <span style={{
                  position: "absolute", bottom: 1, right: 1,
                  width: 11, height: 11, borderRadius: "50%",
                  background: "#31a24c", border: "2px solid #0f1117",
                  boxShadow: "0 0 0 2px rgba(49,164,76,0.35)",
                  animation: "pulse-green 2s infinite",
                }} />
              </div>         <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: th.text, lineHeight: 1.2 }}>{adminData?.fullNames || adminUsername}</div>
                <div style={{ fontSize: 10, color: th.sub, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#31a24c", display: "inline-block", animation: "pulse-green 2s infinite" }} />
                  {isSuperAdmin
                    ? <span style={{ background: "linear-gradient(135deg,#f7971e,#f44336)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontWeight: 800, fontSize: 10 }}>SUPERADMIN</span>
                    : "Online"}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setIsDark(v => !v)}
                style={{ background: th.input, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                {isDark
                  ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#f7971e" }}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
                  : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#6366f1" }}><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>
                }
              </button>
              {chatMode === "users" && (
                <button onClick={() => setShowNewConvo(v => !v)}
                  style={{ background: showNewConvo ? "rgba(0,132,255,0.2)" : th.input, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#e8e8f0" : "#0f0f1a"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </button>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: 4, marginBottom: 12, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 14, padding: 4, border: `1px solid ${th.border}` }}>
            {[
              { key: "users", label: <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg> Users</>, color: "#0084ff" },
              { key: "admins", label: <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M12 14c-5 0-8 2-8 3v1h16v-1c0-1-3-3-8-3z" /></svg> Admins</>, color: "#7c3aed" },
            ].map(tab => (
              <button key={tab.key}
                onClick={() => { setChatMode(tab.key); setActiveUser(null); setActiveAdminChat(null); setMessages([]); setSearch(""); }}
                style={{
                  flex: 1, border: "none", borderRadius: 10, padding: "7px 6px",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  background: chatMode === tab.key
                    ? (tab.key === "admins" ? "linear-gradient(135deg,#7c3aed,#db2777)" : "linear-gradient(135deg,#0084ff,#006fde)")
                    : "transparent",
                  color: chatMode === tab.key ? "#fff" : th.sub,
                  transition: "all 0.2s",
                  boxShadow: chatMode === tab.key ? `0 2px 10px ${tab.color}44` : "none",
                }}>
                {tab.label}
                {tab.key === "admins" && onlineCount > 0 && (
                  <span style={{ marginLeft: 5, background: "rgba(49,164,76,0.9)", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 9, fontWeight: 800 }}>
                    {onlineCount} online
                  </span>
                )}
              </button>
            ))}
          </div>

          {showNewConvo && chatMode === "users" && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10, animation: "slide-up 0.2s ease" }}>
              <input value={newUserInput} onChange={e => setNewUserInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && startNewConversation()}
                placeholder="Enter username..." autoFocus
                style={{ flex: 1, borderRadius: 20, border: "1.5px solid #0084ff", padding: "8px 14px", fontSize: 13, outline: "none", background: th.input, color: th.text, fontFamily: "inherit" }} />
              <button onClick={startNewConversation}
                style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Go
              </button>
            </div>
          )}

          <div style={{ position: "relative" }}>
            <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", opacity: 0.4 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#e8e8f0" : "#0f0f1a"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder={chatMode === "admins" ? "Search admins..." : "Search conversations..."}
              style={{ width: "100%", boxSizing: "border-box", background: th.input, border: "none", borderRadius: 22, padding: "9px 14px 9px 34px", fontSize: 13, outline: "none", color: th.text, fontFamily: "inherit" }} />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px 0" }}>
          {chatMode === "users" ? (
            allUsers.length === 0 ? (
              <div style={{ textAlign: "center", color: th.sub, padding: "32px 16px", fontSize: 13 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, color: th.sub, marginBottom: 8 }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>Loading users...              </div>
            ) : allUsers
              .filter(u => (u.fullNames || u.username || u.email || "").toLowerCase().includes(search.toLowerCase()))
              .map((u, i) => {
                const identifier = u.email ?? u.username;
                const conv = conversations.find(c => c.username === identifier);
                const hasMsg = !!conv;
                const unread = conv?.unreadCount ?? 0;
                const lastMsg = conv?.lastMessage ?? null;
                const isActive = activeUser === identifier;
                return (
                  <div key={identifier} style={{ animation: `slide-in 0.2s ease ${i * 0.03}s both` }}>
                    <div onClick={() => { setChatMode("users"); selectConversation(identifier); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 11,
                        padding: "8px 12px", borderRadius: 14, cursor: "pointer",
                        background: isActive ? (isDark ? "rgba(0,132,255,0.15)" : "rgba(0,132,255,0.1)") : "transparent",
                        borderLeft: `3px solid ${isActive ? "#0084ff" : "transparent"}`,
                        transition: "all 0.15s", opacity: hasMsg ? 1 : 0.5,
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                    >
                      <Avatar name={u.username ?? u.email} size={44} picUrl={userPics[u.username ?? u.email]} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: unread ? 800 : 600, fontSize: 14, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 130 }}>
                            {u.fullNames || u.username}
                          </span>
                          {unread > 0 && (
                            <span style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", borderRadius: "50%", width: 20, height: 20, fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 2px 8px rgba(0,132,255,0.5)" }}>
                              {unread > 9 ? "9+" : unread}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: hasMsg ? (unread ? (isDark ? "#c0c0d8" : "#0f0f1a") : th.sub) : th.sub, fontWeight: unread ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lastMsg
                            ? (lastMsg.startsWith("[IMAGE]") ? "📷 Photo" : lastMsg)
                            : (hasMsg ? "..." : "No messages yet")}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
          ) : (
            filteredAdmins.length === 0 ? (
              <div style={{ textAlign: "center", color: th.sub, padding: "32px 16px", fontSize: 13 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, color: th.sub, marginBottom: 8 }}><circle cx="12" cy="8" r="4" /><path d="M12 14c-5 0-8 2-8 3v1h16v-1c0-1-3-3-8-3z" /></svg>                {adminList.length === 0 ? "Loading admins..." : "No admins found"}
              </div>
            ) : filteredAdmins.map((admin, i) => (
              <div key={admin.username} style={{ animation: `slide-in 0.2s ease ${i * 0.03}s both` }}>
                <AdminConvItem
                  admin={admin}
                  active={activeAdminChat === admin.username}
                  onClick={() => selectAdminChat(admin.username)}
                  isDark={isDark}
                  picUrl={resolvePic(admin.username)}
                  isOnline={adminOnline[admin.username] ?? false}
                />
              </div>
            ))
          )}
        </div>

        <div style={{ padding: "10px 16px 6px", borderTop: `1px solid ${th.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: th.sub, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {chatMode === "admins"
              ? `⚙️ ${adminList.length} admin${adminList.length !== 1 ? "s" : ""}`
              : `👥 ${allUsers.length} users · ${conversations.length} with messages`}
          </div>
        </div>
      </div>

      {/* ══════════════ CHAT AREA ══════════════ */}
      {activeTarget ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", background: th.bg, minWidth: 0 }}>

          {/* Header */}
          <div style={{ padding: "12px 20px", borderBottom: `1px solid ${th.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: th.header, backdropFilter: "blur(20px)", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar name={activeTarget} size={46} online={isAdminChatMode ? (adminOnline[activeTarget] ?? false) : true} picUrl={resolvePic(activeTarget)} isAdmin={isAdminChatMode} />
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 15, color: th.text }}>
                    {isAdminChatMode ? (activeTargetInfo?.fullNames || activeTarget) : (activeTargetInfo?.displayName || activeTarget)}
                  </div>
                  {isAdminChatMode && (
                    <span style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.2),rgba(219,39,119,0.15))", border: "1px solid rgba(124,58,237,0.3)", color: isDark ? "#a78bfa" : "#7c3aed", fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 10, letterSpacing: "0.06em", textTransform: "uppercase" }}>Admin</span>
                  )}
                </div>
                <div style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
                  {isAdminChatMode ? (
                    adminOnline[activeTarget]
                      ? <><span style={{ animation: "pulse-green 2s infinite", width: 6, height: 6, borderRadius: "50%", background: "#31a24c", display: "inline-block" }} /><span style={{ color: "#31a24c" }}>Online</span></>
                      : <><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6b7280", display: "inline-block" }} /><span style={{ color: th.sub }}>Offline</span></>
                  ) : (
                    <><span style={{ animation: "pulse-green 2s infinite", width: 6, height: 6, borderRadius: "50%", background: "#31a24c", display: "inline-block" }} /><span style={{ color: "#31a24c" }}>Active now</span></>
                  )}
                </div>
              </div>
            </div>

            {/* User chat claim controls */}
            {!isAdminChatMode && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {isSuperAdmin ? (
                  <>
                    <div style={{ background: claimStatus.assigned ? "rgba(34,197,94,0.1)" : "rgba(245,158,11,0.1)", border: `1.5px solid ${claimStatus.assigned ? "rgba(34,197,94,0.4)" : "rgba(245,158,11,0.4)"}`, borderRadius: 20, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: claimStatus.assigned ? "#22c55e" : "#f59e0b", display: "flex", alignItems: "center", gap: 6 }}>
                      {claimStatus.assigned ? <><span>✅</span><strong>{claimStatus.assignedTo?.replace(/^admin_/i, "")}</strong></> : "⚠️ Unassigned"}
                    </div>
                    <button onClick={() => setShowForwardModal(true)} style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      ✏️ {claimStatus.assigned ? "Change" : "Assign"}
                    </button>
                    <button onClick={() => setShowForwardModal(true)} style={{ background: "linear-gradient(135deg,#f7971e,#f44336)", color: "#fff", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      ⚡ Forward
                    </button>
                    {claimStatus.assigned && (
                      <button onClick={handleUnassign} style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 20, padding: "8px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                        🗑️ Unassign
                      </button>
                    )}
                  </>
                ) : claimLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: th.sub }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid transparent", borderTop: "2px solid #0084ff", animation: "spin 0.8s linear infinite" }} />
                    Checking...
                  </div>
                ) : !claimStatus.assigned ? (
                  <button onClick={claimConversation} disabled={claiming}
                    style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 20, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: claiming ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 7, animation: "pulse-blue 2s infinite", boxShadow: "0 4px 15px rgba(0,132,255,0.4)" }}>
                    {claiming ? (
                      <>
                        <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2.5px solid rgba(255,255,255,0.3)", borderTop: "2.5px solid #fff", animation: "spin 0.8s linear infinite" }} />
                        Claiming...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                          <path d="M16 11l2 2 4-4" strokeWidth="2.5" />
                        </svg>
                        Claim Chat
                      </>
                    )}
                  </button>
                ) : isMyClaim ? (
                  <div style={{ background: "rgba(34,197,94,0.1)", border: "1.5px solid rgba(34,197,94,0.4)", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: "#22c55e" }}>
                    ✅ Claimed by me
                  </div>
                ) : (
                  <div style={{ background: "rgba(247,151,30,0.1)", border: "1.5px solid rgba(247,151,30,0.4)", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 600, color: "#f7971e" }}>
                    🔒 {claimStatus.assignedTo?.replace(/^admin_/i, "")}

                  </div>
                )}
              </div>
            )}

            {isAdminChatMode && (
              <div style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.18),rgba(219,39,119,0.12))", border: "1px solid rgba(124,58,237,0.35)", borderRadius: 20, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: isDark ? "#a78bfa" : "#7c3aed", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 2px 12px rgba(124,58,237,0.15)" }}>
                <span style={{ fontSize: 14 }}>🔐</span>
                <div>
                  <div>Private Admin Chat</div>
                  <div style={{ fontSize: 9, fontWeight: 500, opacity: 0.7 }}>Only you two can see this</div>
                </div>
              </div>
            )}
          </div>

          {/* Privacy banner for admin DMs */}
          {isAdminChatMode && (
            <div style={{ padding: "7px 20px", background: isDark ? "rgba(124,58,237,0.06)" : "rgba(124,58,237,0.04)", borderBottom: `1px solid rgba(124,58,237,0.15)`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <span style={{ fontSize: 11 }}>🔒</span>
              <span style={{ fontSize: 11, color: isDark ? "rgba(167,139,250,0.7)" : "#7c3aed", fontWeight: 600 }}>
                Messages in this chat are private — only you and <strong>{activeAdminChat?.replace(/^admin_/i, "")}</strong> can see them
              </span>
            </div>
          )}

          {/* Viewer banner for user chats */}
          {!isAdminChatMode && isViewer && !claimLoading && (
            <div style={{ padding: "12px 20px", background: "rgba(247,151,30,0.06)", borderBottom: `2px solid rgba(247,151,30,0.4)`, display: "flex", alignItems: "center", gap: 12, animation: "slide-up 0.2s ease" }}>
              <Avatar
                name={claimStatus.assignedTo ?? "A"}
                size={36}
                picUrl={(() => {
                  const raw = claimStatus.assignedTo ?? "";
                  const clean = raw.replace(/^admin_/i, "");
                  const prefixed = `admin_${clean}`;
                  return (
                    userPics[prefixed] ??
                    userPics[clean] ??
                    userPics[raw] ??
                    null
                  );
                })()}
                isAdmin
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#f7971e", marginBottom: 2 }}>
                  Handled by {claimStatus.assignedTo?.replace(/^admin_/i, "")}                </div>
                <div style={{ fontSize: 11, color: th.sub }}>
                  You are in view-only mode.
                </div>
              </div>
            </div>
          )}

          {/* Pinned message banner */}
          {pinnedMsg && (
            <div style={{ padding: "9px 20px", background: isDark ? "rgba(247,151,30,0.06)" : "rgba(247,151,30,0.05)", borderBottom: `1px solid rgba(247,151,30,0.25)`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ animation: "wiggle 2s infinite", fontSize: 14 }}>📌</span>
              <span style={{ flex: 1, fontSize: 13, color: th.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <strong>Pinned:</strong>{" "}
                {(pinnedMsg.content ?? "").startsWith("[IMAGE]")
                  ? "📷 Photo"
                  : (pinnedMsg.content ?? "").replace(/^↩ ".*?" — /, "")}
              </span>
              <button onClick={() => handlePin(pinnedMsg.messageId)} style={{ background: "none", border: "none", cursor: "pointer", color: th.sub, fontSize: 16 }}>✕</button>
            </div>
          )}

          {/* Messages list */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px", display: "flex", flexDirection: "column", minHeight: 0, background: th.bg }}>
            {loading ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, color: th.sub }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: isAdminChatMode ? "#7c3aed" : "#0084ff", animation: `typing-dot 1.2s infinite ${i * 0.2}s` }} />)}
                </div>
                <span style={{ fontSize: 13 }}>Loading messages...</span>
              </div>
            ) : mergedMessages.length === 0 ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
                <div style={{ animation: "glow-bounce 2.5s ease-in-out infinite" }}>
                  <Avatar name={activeTarget} size={90} picUrl={userPics[activeTarget]} isAdmin={isAdminChatMode} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 20, color: th.text }}>
                  {(activeTarget || "").replace(/^admin_/i, "")}
                </div>
                {isAdminChatMode && (
                  <div style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.15),rgba(219,39,119,0.1))", border: "1px solid rgba(124,58,237,0.25)", borderRadius: 16, padding: "10px 20px", fontSize: 12, color: isDark ? "#a78bfa" : "#7c3aed", fontWeight: 600 }}>🔐 Internal admin-only conversation</div>
                )}
                <div style={{ fontSize: 13, color: th.sub }}>
                  {isAdminChatMode ? "Say hi to your fellow admin! 👋" : "No messages yet. Start the conversation!"}
                </div>
              </div>
            ) : (
              mergedMessages.map((msg, i) => {
                const showDay = i === 0 || formatDay(mergedMessages[i - 1]?.timestamp) !== formatDay(msg.timestamp);
                const myMsg = isMyMessage(msg);
                return (
                  <div key={msg.messageId ?? i}>
                    {showDay && (
                      <div style={{ textAlign: "center", margin: "14px 0 8px" }}>
                        <span style={{ background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)", backdropFilter: "blur(10px)", color: th.sub, fontSize: 11, fontWeight: 700, padding: "4px 14px", borderRadius: 20 }}>
                          {formatDay(msg.timestamp)}
                        </span>
                      </div>
                    )}
                    <MessageBubble
                      msg={msg}
                      isMyMsg={myMsg}
                      prevMsg={mergedMessages[i - 1]}
                      nextMsg={mergedMessages[i + 1]}
                      onReply={setReplyTo}
                      onDelete={handleDelete}
                      onPin={handlePin}
                      onReact={handleReact}
                      isDark={isDark}
                      pinnedId={pinnedMessageId}
                      activeUser={activeTarget} userPicUrl={myMsg
                        ? (adminPic ?? resolvePic(msg.admin))
                        : (resolvePic(activeTarget) ?? resolvePic(msg.user))
                      }
                      canDelete={myMsg || isSuperAdmin}
                    />
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Reply bar */}
          {replyTo && canSend && <ReplyBar replyTo={replyTo} onCancel={() => setReplyTo(null)} isDark={isDark} />}

          {/* Image preview */}
          {imageFile && canSend && (
            <div style={{ padding: "8px 18px 4px", borderTop: `1px solid ${th.border}`, background: th.header }}>
              <ImagePreview file={imageFile} onRemove={() => setImageFile(null)} />
            </div>
          )}

          {/* Input bar */}
          <div style={{
            padding: "12px 18px", borderTop: `1px solid ${isAdminChatMode ? "rgba(124,58,237,0.2)" : th.border}`,
            display: "flex", alignItems: "flex-end", gap: 10,
            background: isAdminChatMode ? (isDark ? "rgba(124,58,237,0.04)" : "rgba(124,58,237,0.02)") : th.header,
            opacity: canSend ? 1 : 0.4, pointerEvents: canSend ? "auto" : "none",
            filter: canSend ? "none" : "grayscale(80%)", transition: "opacity 0.3s, filter 0.3s",
          }}>
            <button onClick={() => fileRef.current?.click()}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 6, borderRadius: "50%", display: "flex", alignItems: "center", flexShrink: 0 }}>
              <svg width="22" height="22" fill="none" stroke={isAdminChatMode ? "#7c3aed" : "#0084ff"} viewBox="0 0 24 24" strokeWidth={1.8}>
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <circle cx="8.5" cy="8.5" r="1.5" fill={isAdminChatMode ? "#7c3aed" : "#0084ff"} stroke="none" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" />
              </svg>
            </button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={e => { const f = e.target.files[0]; if (f) setImageFile(f); e.target.value = ""; }} />

            <div style={{ flex: 1, background: th.input, borderRadius: 24, padding: "10px 18px", display: "flex", alignItems: "center", border: `1px solid ${isAdminChatMode ? "rgba(124,58,237,0.2)" : th.border}` }}>
              <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder={
                  isAdminChatMode
                    ? `Message ${activeAdminChat?.replace(/^admin_/i, "")}... (Admin only 🔐)`

                    : (!canSend ? "🔒 Claim chat first..."
                      : imageFile ? "Add a caption..."
                        : replyTo ? "Write a reply..."
                          : `Message ${activeUser}...`)
                }
                rows={1}
                style={{ flex: 1, border: "none", outline: "none", background: "transparent", resize: "none", fontSize: 14, color: th.text, lineHeight: 1.5, maxHeight: 120, overflow: "auto", fontFamily: "inherit" }} />
            </div>

            {/* FIX: corrected send/like button logic */}
            {canSendInput ? (
              <button onClick={imageFile ? sendImage : sendMessage}
                style={{ background: isAdminChatMode ? "linear-gradient(135deg,#7c3aed,#db2777)" : "linear-gradient(135deg,#0084ff,#006fde)", border: "none", borderRadius: "50%", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: isAdminChatMode ? "0 4px 16px rgba(124,58,237,0.5)" : "0 4px 16px rgba(0,132,255,0.5)", flexShrink: 0, transition: "transform 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              >
                {sending || uploadingImage
                  ? <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2.5px solid rgba(255,255,255,0.3)", borderTop: "2.5px solid #fff", animation: "spin 0.8s linear infinite" }} />
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>}
              </button>
            ) : (
              <button onClick={() => sendMessage()}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 24, padding: 4 }}>👍</button>
            )}
          </div>
        </div>

      ) : (
        /* Empty state */
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18, background: th.bg, padding: 40 }}>
          <div style={{ position: "relative", marginBottom: 8 }}>
            <div style={{ width: 96, height: 96, borderRadius: "50%", background: chatMode === "admins" ? "linear-gradient(135deg, #7c3aed 0%, #db2777 100%)" : "linear-gradient(135deg, #0084ff 0%, #00c6ff 50%, #0084ff 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: chatMode === "admins" ? "0 12px 40px rgba(124,58,237,0.5)" : "0 12px 40px rgba(0,132,255,0.5)", animation: "glow-bounce 3s ease-in-out infinite" }}>
              <svg width="52" height="52" viewBox="0 0 48 48" fill="white">
                {chatMode === "admins"
                  ? <path d="M24 4C12.95 4 4 12.28 4 22.5c0 5.63 2.68 10.66 6.9 14.07V44l6.77-3.72A21.6 21.6 0 0024 41c11.05 0 20-8.28 20-18.5S35.05 4 24 4zm-1 27l-7-7 2.83-2.83L23 25.34l8.17-8.17L34 20l-11 11z" />
                  : <path d="M24 4C12.95 4 4 12.28 4 22.5c0 5.63 2.68 10.66 6.9 14.07V44l6.77-3.72A21.6 21.6 0 0024 41c11.05 0 20-8.28 20-18.5S35.05 4 24 4zm2.1 24.92l-5.1-5.44-9.95 5.44 10.95-11.63 5.23 5.44 9.82-5.44-10.95 11.63z" />}
              </svg>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 800, color: th.text }}>
              {chatMode === "admins" ? "Admin Chat" : "Messages"}
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: th.sub, maxWidth: 280, lineHeight: 1.6 }}>
              {chatMode === "admins"
                ? `${adminList.length} admin${adminList.length !== 1 ? "s" : ""} available · ${onlineCount} online now`
                : "Select a conversation from the sidebar or start a new one"}
            </p>
          </div>
          {chatMode === "users" && (
            <button onClick={() => setShowNewConvo(true)}
              style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 28, padding: "13px 32px", fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 20px rgba(0,132,255,0.5)", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px) scale(1.03)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0) scale(1)"; }}>
              ✉️ New Message
            </button>
          )}
        </div>
      )}
    </div>
  );
}